import React from 'react';

const TYPE_LABELS = {
  raw: '原始',
  disclosure: '披露',
  calculated: '计算',
  inferred: '推断',
  missing: '缺失'
};

function present(value) {
  return value != null && value !== '' && value !== '—';
}

function display(value, suffix = '') {
  if (!present(value)) return '—';
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (typeof value === 'object') return JSON.stringify(value);
  return `${value}${suffix}`;
}

function num(value, digits = 2, suffix = '') {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(digits)}${suffix}` : '—';
}

function row(label, value, type, source, date, note) {
  return { label, value: display(value), type, source, date, note };
}

function LedgerTable({ rows }) {
  return (
    <div className="ledger-table-scroll">
      <table className="ledger-table">
        <thead>
          <tr><th>字段</th><th>当前值</th><th>性质</th><th>来源</th><th>数据日期</th><th>备注</th></tr>
        </thead>
        <tbody>
          {(rows || []).map((item, index) => {
            const missing = !present(item.value) || item.value === '—';
            const type = missing ? 'missing' : item.type;
            return (
              <tr className={missing ? 'is-missing' : ''} key={`${item.label}-${index}`}>
                <td>{item.label}</td>
                <td>{item.value}</td>
                <td><span className={`ledger-type type-${type}`}>{TYPE_LABELS[type] || type}</span></td>
                <td>{item.source || '—'}</td>
                <td>{item.date || '—'}</td>
                <td>{item.note || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LedgerSection({ title, subtitle, rows, children }) {
  const total = rows?.length || 0;
  const filled = (rows || []).filter((item) => present(item.value) && item.value !== '—').length;
  return (
    <section className="ledger-section">
      <div className="ledger-section-head">
        <div><h4>{title}</h4>{subtitle ? <p>{subtitle}</p> : null}</div>
        {total ? <span>{filled}/{total} 有值</span> : null}
      </div>
      {rows?.length ? <LedgerTable rows={rows} /> : null}
      {children}
    </section>
  );
}

function AllocationTable({ rows, period }) {
  if (!rows?.length) return null;
  return (
    <div className="ledger-subtable">
      <h5>实际资产配置</h5>
      <table><thead><tr><th>类别</th><th>占比</th><th>金额（万元）</th><th>原文页</th><th>性质</th></tr></thead>
        <tbody>{rows.map((item, index) => (
          <tr key={`${item.key || item.name}-${index}`}>
            <td>{item.name || item.asset_class || '—'}</td>
            <td>{num(item.pct, 2, '%')}</td>
            <td>{num(item.amount_wan, 2)}</td>
            <td>{item.page ? `第${item.page}页` : '—'}</td>
            <td><span className="ledger-type type-disclosure">披露</span> · {period || '—'}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function HoldingsTable({ rows, period }) {
  if (!rows?.length) return null;
  return (
    <div className="ledger-subtable">
      <h5>前十大持仓</h5>
      <table><thead><tr><th>序号</th><th>资产名称</th><th>金额（万元）</th><th>占比</th><th>原文页</th><th>性质</th></tr></thead>
        <tbody>{rows.slice(0, 10).map((item, index) => (
          <tr key={`${item.name || item.bond_name}-${index}`}>
            <td>{item.rank || index + 1}</td>
            <td>{item.name || item.bond_name || '—'}</td>
            <td>{num(item.amount_wan, 2)}</td>
            <td>{num(item.pct, 2, '%')}</td>
            <td>{item.page ? `第${item.page}页` : '—'}</td>
            <td><span className="ledger-type type-disclosure">披露</span> · {period || '—'}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function EvidenceList({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <div className="ledger-evidence-block">
      <h5>{title}</h5>
      <ul>{rows.map((item, index) => (
        <li key={`${item.field || item}-${index}`}>
          {typeof item === 'string' ? item : (
            <><strong>{item.field || '依据'}</strong>{item.page ? ` · 第${item.page}页` : ''}：{item.quote || item.description || '—'}</>
          )}
        </li>
      ))}</ul>
    </div>
  );
}

function buildStaticRows(profile) {
  const date = profile?.last_update_time;
  return [
    row('产品编号', profile?.product_id, 'raw', 'product_static', date),
    row('产品名称', profile?.product_name, 'raw', 'product_static', date),
    row('产品全称', profile?.product_full_name, 'raw', 'product_static', date),
    row('销售代码', profile?.sale_code, 'raw', 'product_static', date),
    row('登记编码', profile?.product_code, 'raw', 'product_static', date),
    row('代销机构', profile?.agent_org, 'raw', 'product_static', date),
    row('管理人', profile?.manager_org, 'raw', 'product_static', date),
    row('产品类型', profile?.product_type, 'raw', 'product_static', date),
    row('产品系列', profile?.product_series, 'raw', 'product_static.product_series', date),
    row('产品分类', profile?.product_category, 'raw', 'product_static.product_category', date),
    row('风险等级', profile?.risk_level, 'raw', 'product_static', date),
    row('存续状态', profile?.status_label || profile?.status, 'raw', 'product_static', date, profile?.status ? `原码值 ${profile.status}` : null),
    row('持有期', profile?.term_text, 'raw', 'product_static', date),
    row('起购金额', profile?.min_purchase, 'raw', 'product_static', date),
    row('成立日', profile?.establish_date, 'raw', 'product_static', date),
    row('上市日', profile?.market_date, 'raw', 'product_static', date),
    row('收益起始日', profile?.income_start_date, 'raw', 'product_static', date),
    row('申购截止', profile?.purchase_deadline, 'raw', 'product_static', date),
    row('确认规则', profile?.confirm_time, 'raw', 'product_static', date),
    row('初始净值', profile?.initial_nav, 'raw', 'product_static', date),
    row('业绩比较基准原文', profile?.benchmark_value, 'raw', 'product_static', date)
  ];
}

function buildNavRows(result) {
  const profile = result?.product_profile || {};
  const perf = result?.performance_stats || {};
  const nav = result?.nav_performance || {};
  const anomaly = nav?.anomaly_review || {};
  const riskAdjusted = result?.risk_adjusted_performance || {};
  const drawdownPath = result?.drawdown_path_analysis || {};
  const date = profile.latest_nav_date;
  return [
    row('最新净值日期', date, 'raw', 'product_dynamic', date),
    row('单位净值', num(profile.latest_unit_nav, 6), 'raw', 'product_dynamic', date),
    row('万份收益', num(profile.latest_daily_income, 2), 'raw', 'product_dynamic', date),
    row('7日万份收益', num(profile.avg_7d_income, 2), 'raw', 'product_dynamic', date),
    row('14日万份收益', num(profile.avg_14d_income, 2), 'raw', 'product_dynamic', date),
    row('7日涨幅', num(profile.latest_return_7d, 2, '%'), 'raw', 'product_dynamic', date),
    row('14日涨幅', num(profile.latest_return_14d, 2, '%'), 'raw', 'product_dynamic', date),
    row('1月涨幅', num(profile.latest_return_1m, 2, '%'), 'raw', 'product_dynamic', date),
    row('7日年化', num(profile.annual_7d_yield, 2, '%'), 'raw', 'product_dynamic', date),
    row('14日年化', num(profile.annual_14d_yield, 2, '%'), 'raw', 'product_dynamic', date),
    row('1月年化', num(profile.annual_1m_yield, 2, '%'), 'raw', 'product_dynamic', date),
    row('3月年化', num(profile.annual_3m_yield, 2, '%'), 'raw', 'product_dynamic', date),
    row('成立以来年化', num(profile.annual_since_yield, 2, '%'), 'raw', 'product_dynamic', date),
    row('历史数据点数', nav.total_points, 'calculated', 'product_dynamic 历史序列', date),
    row('126日最大回撤', num(perf.max_drawdown_126d_bp, 1, 'bp'), 'calculated', '净值历史计算', date),
    row('30日波动', num(perf.volatility_30d_bp, 2, 'bp'), 'calculated', '净值历史计算', date),
    row('30日上涨日占比', num(perf.win_rate_30d_pct, 1, '%'), 'calculated', '净值历史计算', date),
    row('近6月收益', num(perf.return_6m_pct, 2, '%'), 'calculated', '净值历史计算', date),
    row('回撤修复能力', nav.recovery_capability?.text, 'calculated', '净值历史计算', date),
    row('净值异常复核结论', anomaly.available ? anomaly.status?.label : null, 'calculated', '日变动MAD稳健识别', date, anomaly.status?.note || anomaly.reason),
    row('大幅变动日数量', anomaly.available ? anomaly.count : null, 'calculated', '日变动MAD稳健识别', date, anomaly.available ? `${anomaly.sample_size}个样本，占${num(anomaly.ratio_pct, 1, '%')}` : null),
    row('大幅变动日净贡献', anomaly.available ? num(anomaly.anomaly_net_contribution_bp, 2, 'bp') : null, 'calculated', '异常日贡献简单加总', date, '用于检查少数日期影响，不是替代收益率'),
    row('风险调整后表现摘要', riskAdjusted.available ? riskAdjusted.summary : null, riskAdjusted.available ? 'calculated' : 'missing', riskAdjusted.version || '短窗口风险效率', date, '不进入综合评分'),
    ...(riskAdjusted.windows || []).map((item) => row(
      `风险效率·${item.label}`,
      item.available ? `收益 ${num(item.cumulative_return_bp, 1, 'bp')}；回撤 ${num(item.max_drawdown_bp, 1, 'bp')}；收益/回撤 ${num(item.return_drawdown_ratio, 2)}` : null,
      item.available ? 'calculated' : 'missing',
      'product_dynamic日变动序列',
      item.end_date || date,
      item.available ? `日波动 ${num(item.volatility_bp, 2, 'bp')}；下行风险 ${num(item.downside_risk_bp, 2, 'bp')}；日均收益/波动 ${num(item.return_volatility_ratio, 2)}；日均收益/下行风险 ${num(item.return_downside_ratio, 2)}；${item.fact}` : item.reason
    )),
    row('全历史回撤路径状态', drawdownPath.available ? drawdownPath.state?.label : null, drawdownPath.available ? 'calculated' : 'missing', drawdownPath.version || '单位净值路径', drawdownPath.end_date || date, drawdownPath.summary),
    row('全历史回撤样本范围', drawdownPath.available ? `${drawdownPath.source_points}个净值点` : null, drawdownPath.available ? 'calculated' : 'missing', 'product_dynamic全部有效单位净值', drawdownPath.end_date || date, drawdownPath.available ? `${drawdownPath.start_date} 至 ${drawdownPath.end_date}` : null),
    row('全历史最大回撤', drawdownPath.available ? num(drawdownPath.max_drawdown_bp, 1, 'bp') : null, drawdownPath.available ? 'calculated' : 'missing', '全部已有单位净值路径', drawdownPath.end_date || date),
    row('全历史回撤与修复次数', drawdownPath.available ? `${drawdownPath.episode_count}次回撤；${drawdownPath.recovered_count}次已修复` : null, drawdownPath.available ? 'calculated' : 'missing', '全部已有单位净值路径', drawdownPath.end_date || date, drawdownPath.average_recovery_days != null ? `已修复回撤平均用时 ${num(drawdownPath.average_recovery_days, 1, '个净值点')}` : null),
    ...(drawdownPath.windows || []).map((item) => row(
      `近期回撤切片·${item.label}`,
      item.available ? `最大 ${num(item.max_drawdown_bp, 1, 'bp')}；当前 ${num(item.current_drawdown_bp, 1, 'bp')}` : null,
      item.available ? 'calculated' : 'missing',
      '全部路径末端切片',
      item.end_date || date,
      item.available ? `${item.episode_count}次回撤；${item.recovered_count}次已修复` : item.reason
    )),
    ...(anomaly.items || []).slice(0, 8).map((item, index) => row(
      `大幅净值变动 ${index + 1}`,
      `${item.date} · ${num(item.change_pct, 4, '%')}`,
      'calculated',
      'product_dynamic日变动序列',
      item.date,
      `${item.pattern}；单位净值 ${num(item.unit_nav, 6)}；万份收益 ${num(item.daily_income, 2)}`
    ))
  ];
}

function buildProspectusRows(disclosure) {
  const date = disclosure?.display_meta?.updated_at;
  return (disclosure?.prospectus?.items || []).map((item) =>
    row(item.label, item.value, item.value ? 'disclosure' : 'missing', '已审核发布说明书', date)
  );
}

function buildReportRows(report) {
  const period = report?.report_period;
  return [
    row('报告期', period, 'disclosure', '已审核发布运作报告', report?.updated_at),
    row('报告类型', report?.report_type, 'disclosure', '运作报告', period),
    row('产品资产净值', report?.total_aum_text, 'disclosure', '运作报告', period),
    row('总份额', report?.total_shares != null ? Number(report.total_shares).toLocaleString() : null, 'disclosure', '运作报告', period),
    row('机构投资者占比', report?.institutional_pct != null ? num(report.institutional_pct, 2, '%') : null, 'disclosure', '运作报告', period),
    row('组合久期', report?.duration_years != null ? num(report.duration_years, 2, '年') : null, 'disclosure', '运作报告', period),
    row('实际杠杆率', report?.leverage_ratio != null ? num(report.leverage_ratio, 2, '%') : null, 'disclosure', '运作报告', period),
    row('本期策略与操作', report?.strategy_summary, 'disclosure', '运作报告', period),
    row('管理人后市展望', report?.performance_attribution?.outlook, 'disclosure', '运作报告', period)
  ];
}

function buildBenchmarkRows(analysis) {
  const tracking = analysis?.tracking_analysis;
  const trackingPrimary = (tracking?.windows || []).find((item) => item.key === tracking?.primary_window && item.available);
  const rows = [
    row('业绩基准原文', analysis?.raw, 'raw', 'product_static.benchmark_value', null),
    row('权重合计', analysis?.weightSum != null ? num(analysis.weightSum, 1, '%') : null, 'calculated', '基准解析器', null),
    row('解析质量', analysis?.parseQuality, 'calculated', `基准解析器 v${analysis?.parserVersion || '—'}`, null),
    row('当日合成基准涨幅', analysis?.synthetic_return_today_text, 'calculated', '基准成分行情加权', null),
    row('产品当日涨幅', analysis?.product_change_today_text, 'raw', 'product_dynamic', null),
    row('相对合成基准', analysis?.vs_synthetic_bp != null ? num(analysis.vs_synthetic_bp, 2, 'bp') : null, 'calculated', '产品涨幅－合成基准', null),
    row('超额稳定性结论', trackingPrimary?.verdict?.label, 'calculated', 'T+N交易日对齐的产品与合成基准', trackingPrimary?.end_market_date, trackingPrimary?.verdict?.note),
    row('累计超额', trackingPrimary?.cumulative_excess_bp != null ? num(trackingPrimary.cumulative_excess_bp, 2, 'bp') : null, 'calculated', trackingPrimary?.label, trackingPrimary?.end_market_date, `${trackingPrimary?.sample_size || 0}个对齐日`),
    row('日跟踪误差', trackingPrimary?.daily_tracking_error_bp != null ? num(trackingPrimary.daily_tracking_error_bp, 3, 'bp') : null, 'calculated', '日度超额样本标准差', trackingPrimary?.end_market_date, '不等于管理能力评价'),
    row('跑赢日占比', trackingPrimary?.excess_hit_rate_pct != null ? num(trackingPrimary.excess_hit_rate_pct, 1, '%') : null, 'calculated', trackingPrimary?.label, trackingPrimary?.end_market_date),
    row('剔除前3偏离日后的累计超额', trackingPrimary?.excess_without_top3_bp != null ? num(trackingPrimary.excess_without_top3_bp, 2, 'bp') : null, 'calculated', '极端日期稳健性检查', trackingPrimary?.end_market_date, `前3日占绝对超额波动 ${num(trackingPrimary?.top3_absolute_share_pct, 1, '%')}`)
  ];
  (analysis?.components || []).forEach((item, index) => {
    rows.push(row(`基准成分 ${index + 1}`, `${item.name || item.label || item.code} · ${num(item.weightPct, 1, '%')}`, item.type === 'unknown' ? 'inferred' : 'calculated', '基准解析器', null, `资产类别 ${item.assetClass || '未识别'}`));
  });
  (analysis?.index_snapshots || []).forEach((item, index) => {
    rows.push(row(`行情成分 ${index + 1}`, item.available === false || item.change_pct == null ? null : num(item.change_pct, 4, '%'), item.change_pct == null ? 'missing' : 'raw', item.data_source || '市场行情', item.trade_date, item.name));
  });
  return rows;
}

function buildMacroRows(result) {
  const macro = result?.dimensions?.macro || {};
  const rateContext = result?.market_factor_context?.rate_indicators || {};
  const indicators = new Map(
    (rateContext.indicators || [])
      .filter((item) => item.selected_for_product !== false)
      .map((item) => [item.key, item])
  );
  const funding = indicators.get('funding_rate')?.value;
  const curve = indicators.get('government_curve');
  const ncd = indicators.get('ncd_yield')?.value;
  const credit = indicators.get('credit_spread');
  const liquidity = rateContext.liquidity_regime;
  const equity = (result?.market_factor_context?.groups || []).find((item) => item.key === 'equity');
  const equityRegime = equity?.equity_regime;
  const marketStress = result?.market_factor_context?.market_stress;
  const transmission = result?.market_pressure_transmission;
  const multifactor = result?.product_market_sensitivity?.multifactor;
  const multifactorRows = multifactor?.available ? [
    row('多因子样本外结论', multifactor.verdict?.label, 'calculated', '标准化岭回归+按时间留出检验', multifactor.holdout_end, multifactor.verdict?.note),
    row('多因子训练期R²', multifactor.train_metrics?.r_squared != null ? num(multifactor.train_metrics.r_squared * 100, 1, '%') : null, 'calculated', '训练样本拟合', multifactor.train_end, `${multifactor.train_size}个样本`),
    row('多因子样本外R²', multifactor.holdout_metrics?.r_squared != null ? num(multifactor.holdout_metrics.r_squared * 100, 1, '%') : null, 'calculated', '后续时间区间检验', multifactor.holdout_end, `${multifactor.holdout_size}个样本；MAE ${num(multifactor.holdout_metrics?.mae_bp, 2, 'bp')}`),
    ...(multifactor.factors || []).map((factor) => row(`多因子标准化系数·${factor.label}`, num(factor.standardized_coefficient, 3), 'calculated', '标准化岭回归', multifactor.train_end, `${factor.code}；${factor.direction}；仅可在模型内比较`))
  ] : [row('多因子联合解释', null, 'missing', '多因子模型', null, multifactor?.reason || '尚未生成')];
  const sensitivityRows = (result?.product_market_sensitivity?.factors || []).flatMap((factor) => {
    const source = `按${result?.product_market_sensitivity?.confirm_time || 'T+N'}对齐的产品净值与${factor.code}`;
    const date = factor.sample_end_market_date;
    const note = `${factor.sample_size || 0}个对齐样本；${factor.relationship_direction || '方向未定'}；置信度${factor.confidence?.label || '较低'}；不代表因果`;
    return [
      row(`${factor.label}历史相关系数`, factor.available ? num(factor.correlation, 3) : null, 'calculated', source, date, note),
      row(`${factor.label}隐含敏感度β`, factor.available ? num(factor.beta, 4) : null, 'calculated', '单因子历史线性拟合', date, factor.beta_displayable ? factor.method_note : '保留原始计算值便于复核；相关关系过弱，页面不对β作经济含义解释'),
      row(`${factor.label}滚动稳定性`, factor.rolling?.label, 'calculated', '20/40/60对齐样本窗口', date, `${factor.rolling?.note || '—'}；大幅净值变动样本 ${factor.anomalies?.count || 0} 个`),
      ...(factor.anomalies?.items || []).slice(0, 5).map((item, index) => row(
        `${factor.label}大幅净值变动 ${index + 1}`,
        `${item.nav_date} · ${num(item.product_pct, 4, '%')}`,
        'calculated',
        'product_dynamic与市场行情对齐复核',
        item.market_date,
        `${factor.code} ${num(item.market_pct, 4, '%')}；${item.quality?.label || '待复核'}；${item.quality?.note || '大幅不等于数据错误'}`
      ))
    ];
  });
  return [
    row('宏观维度得分', macro.score, 'calculated', '债市晴雨表与规则引擎', null),
    ...(macro.signals || []).map((item, index) => row(`宏观信号 ${index + 1}`, item, 'calculated', '债市晴雨表', null)),
    ...(funding ? [row('FDR007', funding.value_pct != null ? num(funding.value_pct, 4, '%') : null, 'raw', '中国货币网', funding.trade_date, `1日 ${num(funding.change_1d_bp, 2, 'bp')}；5日 ${num(funding.change_5d_bp, 2, 'bp')}；资金面代理，不等同全天DR007`)] : []),
    ...(curve?.curve || []).map((item) => row(`国债到期收益率 ${item.tenor}`, item.value_pct != null ? num(item.value_pct, 4, '%') : null, 'raw', '中国货币网收益率曲线', item.trade_date, `5日变化 ${num(item.change_5d_bp, 2, 'bp')}`)),
    ...((curve?.curve_spreads || []).map((item) => row(`国债期限利差 ${item.label}`, item.available ? num(item.value_bp, 2, 'bp') : null, item.available ? 'calculated' : 'missing', '国债收益率曲线', item.trade_date || curve.value?.trade_date, item.available ? `1日 ${num(item.change_1d_bp, 2, 'bp')}；5日 ${num(item.change_5d_bp, 2, 'bp')}；20日 ${num(item.change_20d_bp, 2, 'bp')}` : null))),
    ...(curve?.curve_shape ? [row('国债收益率曲线形态', curve.curve_shape.label, 'inferred', '10Y−1Y期限利差变化', curve.value?.trade_date, curve.curve_shape.detail)] : []),
    ...(ncd ? [row('AAA同业存单到期收益率 1Y', ncd.value_pct != null ? num(ncd.value_pct, 4, '%') : null, 'raw', '中国货币网收益率曲线', ncd.trade_date, `5日变化 ${num(ncd.change_5d_bp, 2, 'bp')}`)] : []),
    ...(liquidity?.available ? [
      row('同业存单财富指数近5日', liquidity.signals?.find((item) => item.key === 'ncd_index')?.change_5d != null ? num(liquidity.signals.find((item) => item.key === 'ncd_index').change_5d, 3, '%') : null, 'calculated', '同业存单财富指数日涨跌累计', liquidity.as_of),
      row('短端流动性状态', liquidity.state?.label, 'inferred', 'FDR007、AAA 1Y存单收益率与同业存单财富指数', liquidity.as_of, liquidity.detail)
    ] : []),
    ...(credit ? [
      ...((credit.credit_layers || []).map((item) => row(`${item.rating}中票到期收益率 3Y`, item.yield?.value_pct != null ? num(item.yield.value_pct, 4, '%') : null, 'raw', '中国货币网收益率曲线', item.yield?.trade_date, `1日 ${num(item.yield?.change_1d_bp, 2, 'bp')}；5日 ${num(item.yield?.change_5d_bp, 2, 'bp')}；20日 ${num(item.yield?.change_20d_bp, 2, 'bp')}`))),
      row('AAA中票信用利差 3Y', credit.value?.value_bp != null ? num(credit.value.value_bp, 2, 'bp') : null, 'calculated', '3Y中票AAA − 3Y国债', credit.value?.trade_date, credit.value ? `5日变化 ${num(credit.value.change_5d_bp, 2, 'bp')}` : null),
      row('AA+中票信用利差 3Y', credit.secondary?.value_bp != null ? num(credit.secondary.value_bp, 2, 'bp') : null, 'calculated', '3Y中票AA+ − 3Y国债', credit.secondary?.trade_date, credit.secondary ? `5日变化 ${num(credit.secondary.change_5d_bp, 2, 'bp')}` : null),
      row('AA+−AAA评级溢价', credit.rating_premium?.value_bp != null ? num(credit.rating_premium.value_bp, 2, 'bp') : null, 'calculated', 'AA+信用利差 − AAA信用利差', credit.rating_premium?.trade_date, credit.rating_premium ? `1日 ${num(credit.rating_premium.change_1d_bp, 2, 'bp')}；5日 ${num(credit.rating_premium.change_5d_bp, 2, 'bp')}；20日 ${num(credit.rating_premium.change_20d_bp, 2, 'bp')}` : null),
      row('信用分层状态', credit.credit_state?.label, 'inferred', 'AAA/AA+ 3Y中票信用利差与评级溢价', credit.value?.trade_date, credit.credit_state?.detail)
    ] : []),
    ...((equityRegime?.indexes || []).map((item) => row(`权益指数 ${item.label}`, item.change_pct != null ? num(item.change_pct, 3, '%') : null, 'raw', item.code, item.trade_date, `近5日 ${num(item.change_5d_pct, 3, '%')}；近20日 ${num(item.change_20d_pct, 3, '%')}`))),
    ...(equityRegime?.available ? [
      row('权益风险偏好状态', equityRegime.state?.label, 'inferred', '代表指数上涨宽度、连续性与风格相对强弱', equityRegime.as_of, `${equityRegime.state?.detail}；${equityRegime.state?.style_detail}`),
      row('权益当日上涨宽度', `${equityRegime.breadth?.today?.positive}/${equityRegime.breadth?.today?.total}`, 'calculated', '同日代表指数上涨数量', equityRegime.as_of),
      row('中小盘相对大盘（近5日）', equityRegime.styles?.small_vs_large_5d_pct != null ? num(equityRegime.styles.small_vs_large_5d_pct, 3, '个百分点') : null, 'calculated', '中证500/1000均值 − 沪深300/上证50均值', equityRegime.as_of),
      row('成长相对宽基（近5日）', equityRegime.styles?.growth_vs_broad_5d_pct != null ? num(equityRegime.styles.growth_vs_broad_5d_pct, 3, '个百分点') : null, 'calculated', '科创100/创业板均值 − 沪深300/中证500/1000均值', equityRegime.as_of)
    ] : []),
    ...((marketStress?.segments || []).filter((segment) => segment.selected_for_product !== false).flatMap((segment) => [
      row(`${segment.label}回撤状态`, segment.state?.label, 'inferred', '相关市场指数全部已入库历史', segment.as_of, segment.conclusion),
      ...(segment.indexes || []).map((item) => row(`${item.label}·市场回撤与波动`, `当前回撤 ${num(item.current_drawdown_pct, 3, '%')}；修复 ${num(item.repair_pct, 1, '%')}`, 'calculated', `${item.code}全部${item.sample_size}个收盘点`, item.as_of, `历史最大回撤 ${num(item.full_history_max_drawdown_pct, 3, '%')}；7/14/30日波动 ${num(item.volatility_7d_pct, 3, '%')} / ${num(item.volatility_14d_pct, 3, '%')} / ${num(item.volatility_30d_pct, 3, '%')}`))
    ])),
    ...(transmission?.factors || []).flatMap((factor) => [
      row(`市场传导·${factor.label}`, factor.effect_label, 'inferred', '已披露暴露 × 公共市场环境', transmission.as_of, `${factor.exposure}；${factor.transmission}`),
      ...Object.entries(factor.actions || {}).map(([key, item]) => row(`市场传导·${factor.label}·${({ buy: '买入', add: '加仓', hold: '持有', reduce: '减仓' })[key]}`, item.text, 'inferred', '条件式操作解释', transmission.as_of, `影响性质：${item.effect}`))
    ]),
    ...multifactorRows,
    ...sensitivityRows
  ];
}

function buildRiskRows(profile) {
  const period = profile?.data_quality?.latest_report_period;
  const rows = [];
  (profile?.factors || []).forEach((factor) => {
    rows.push(row(factor.label, factor.risk_score != null ? `${factor.level_label} · ${factor.risk_score}/100` : null, 'calculated', `风险暴露模型 ${profile.version || 'v1'}`, period, `可信度${factor.confidence?.label || '—'}；${factor.assessment || ''}`));
    (factor.evidence || []).forEach((item, index) => rows.push(row(`${factor.label}依据 ${index + 1}`, item, /^推断值/.test(item) ? 'inferred' : /^缺失/.test(item) ? 'missing' : 'calculated', '披露数据二次分析', period)));
  });
  return rows;
}

function buildDecisionRows(result) {
  const strictPeer = result?.strict_peer_comparison;
  const trend = result?.performance_trend;
  const rows = [
    row('综合分', result?.score, 'calculated', '决策引擎', null, '当前仅作辅助参考'),
    row('当前动作标签', result?.label, 'inferred', '决策引擎', null),
    row('动作置信度', result?.confidence, 'inferred', '决策引擎', null),
    row('产品自身维度', result?.dimensions?.intrinsic?.score, 'calculated', '净值与同类表现', result?.product_profile?.latest_nav_date),
    row('宏观环境维度', result?.dimensions?.macro?.score, 'calculated', '债市环境', null),
    row('个人持仓维度', result?.dimensions?.personal?.score, 'calculated', '本地持仓', null),
    row('当前组合占比', result?.dimensions?.personal?.weight_pct != null ? num(result.dimensions.personal.weight_pct, 1, '%') : null, 'raw', '本地持仓', null),
    row('运营状态维度', result?.dimensions?.ops?.score, 'calculated', '产品状态与申赎信息', null),
    row('同类收益分位', result?.peer_percentile != null ? num(result.peer_percentile, 0, '%') : null, 'calculated', result?.peer_metric || '同类指标', result?.product_profile?.latest_nav_date),
    row('决策摘要', result?.summary, 'inferred', '决策规则与洞察', null)
  ];
  if (strictPeer) {
    rows.push(row('严格同类选用边界', strictPeer.selected_label, 'calculated', '同系列→同风险→同期限→同确认时滞', null, `${strictPeer.selected_sample_size || 0}个产品；${strictPeer.caveat || ''}`));
    (strictPeer.metrics || []).filter((metric) => metric.available).forEach((metric) => {
      rows.push(row(`严格同类·${metric.label}`, `${num(metric.value, 2, metric.suffix)}；第${metric.rank_from_top}/${metric.sample_size}`, 'calculated', strictPeer.selected_label, null, `中位数 ${num(metric.median_value, 2, metric.suffix)}；分位 ${num(metric.percentile, 0, '%')}`));
      (metric.trend?.windows || []).filter((window) => window.available).forEach((window) => {
        rows.push(row(
          `严格同类趋势·${metric.label}·${window.label}`,
          `${num(window.start_avg_percentile, 0, '%')} → ${num(window.end_avg_percentile, 0, '%')}`,
          'calculated',
          'product_dynamic历史排名',
          window.end_date,
          `${window.verdict?.label || '—'}；变化 ${num(window.delta_percentile, 0, '个百分点')}；${window.sample_size}个排名点`
        ));
      });
    });
    rows.push(row(
      '严格同类替代候选',
      strictPeer.alternatives?.available ? `${strictPeer.alternatives.items?.length || 0}个` : null,
      strictPeer.alternatives?.available ? 'calculated' : 'missing',
      strictPeer.alternatives?.boundary_label || '严格同类边界',
      result?.product_profile?.latest_nav_date,
      strictPeer.alternatives?.reason || strictPeer.alternatives?.caveat
    ));
  }
  if (trend) {
    rows.push(row(
      '近期表现状态',
      trend.available ? trend.state_label : null,
      trend.available ? 'inferred' : 'missing',
      trend.version || '前后同长度窗口',
      trend.recent?.end_date || result?.product_profile?.latest_nav_date,
      `${trend.summary || '样本不足'}；不进入综合评分`
    ));
    (trend.metrics || []).filter((metric) => metric.available).forEach((metric) => {
      rows.push(row(
        `近期趋势·${metric.label}`,
        `${num(metric.previous, metric.unit === 'pct' ? 1 : 2, metric.unit)} → ${num(metric.recent, metric.unit === 'pct' ? 1 : 2, metric.unit)}`,
        'calculated',
        trend.basis_label,
        trend.recent?.end_date,
        `${metric.verdict}；变化 ${num(metric.delta, metric.unit === 'pct' ? 1 : 2, metric.unit)}`
      ));
    });
    rows.push(row(
      '近期趋势可信度',
      trend.confidence_label,
      trend.available ? 'inferred' : 'missing',
      '产品自身窗口 + 严格同类 + 异常日复核',
      trend.recent?.end_date,
      trend.anomaly_caution || trend.peer_corroboration?.label || trend.method
    ));
  }
  if (result?.evidence_consistency) {
    const evidence = result.evidence_consistency;
    rows.push(row('证据一致性结论', evidence.state?.label, 'inferred', '六个独立证据域', result?.product_profile?.latest_nav_date, `${evidence.summary}；覆盖率${evidence.coverage?.pct || 0}%；不进入评分`));
    (evidence.items || []).forEach((item) => rows.push(row(`证据域·${item.label}`, item.fact, item.direction === 'unknown' ? 'missing' : 'inferred', item.source, result?.product_profile?.latest_nav_date, `${item.direction}；${item.strength}；${item.note || ''}`)));
    Object.entries(evidence.actions || {}).forEach(([key, text]) => rows.push(row(`证据一致性·${({ buy: '买入', add: '加仓', hold: '持有', reduce: '减仓' })[key]}`, text, 'inferred', '证据一致性条件解释', result?.product_profile?.latest_nav_date)));
  }
  Object.entries(result?.risk_exposure_profile?.action_summary || {}).forEach(([key, item]) => {
    const labels = { buy: '风险画像对买入', add: '风险画像对加仓', hold: '风险画像对持有', reduce: '风险画像对减仓' };
    rows.push(row(labels[key] || key, item.label, 'inferred', '六类风险操作映射', result?.risk_exposure_profile?.data_quality?.latest_report_period, `支持 ${item.counts?.support || 0} · 谨慎 ${item.counts?.caution || 0}`));
  });
  return rows;
}

export default function ProductDataLedgerPanel({ result }) {
  if (!result) return null;
  const profile = result.product_profile || {};
  const disclosure = result.product_disclosure || result.document_insight || {};
  const report = disclosure.latest_report;
  const constraintsEvidence = disclosure.prospectus?.constraints?.evidence || [];
  const groups = [
    buildStaticRows(profile),
    buildNavRows(result),
    buildProspectusRows(disclosure),
    buildReportRows(report),
    buildBenchmarkRows(result.benchmark_analysis),
    buildMacroRows(result),
    buildRiskRows(result.risk_exposure_profile),
    buildDecisionRows(result)
  ];
  const total = groups.reduce((sum, rows) => sum + rows.length, 0);
  const filled = groups.reduce((sum, rows) => sum + rows.filter((item) => present(item.value) && item.value !== '—').length, 0);
  const missing = total - filled + (disclosure.data_gaps?.length || 0);

  return (
    <div className="product-data-ledger">
      <div className="ledger-overview">
        <div><span>数据组</span><strong>8</strong></div>
        <div><span>字段与依据</span><strong>{total}</strong></div>
        <div><span>已有值</span><strong>{filled}</strong></div>
        <div><span>缺失/待补</span><strong>{missing}</strong></div>
      </div>
      <p className="ledger-intro">这里展示当前决策站实际拥有的数据底稿；“推断”与“计算”不会伪装成产品原始披露。</p>

      <LedgerSection title="1. 产品静态信息" subtitle="产品身份、分类、期限、状态与基础规则" rows={groups[0]} />
      <LedgerSection title="2. 净值、收益与历史统计" subtitle="最新动态值以及由历史序列计算的回撤、波动和修复能力" rows={groups[1]} />
      <LedgerSection title="3. 产品说明书" subtitle="仅使用人工审核并发布的说明书结构化结果" rows={groups[2]}>
        <EvidenceList title="说明书约束原文证据" rows={constraintsEvidence} />
      </LedgerSection>
      <LedgerSection title="4. 最新运作报告" subtitle="组合快照、实际资产配置、主要持仓与管理人观点" rows={groups[3]}>
        <AllocationTable rows={report?.asset_allocation} period={report?.report_period} />
        <HoldingsTable rows={report?.top_holdings} period={report?.report_period} />
      </LedgerSection>
      <LedgerSection title="5. 业绩基准与成分行情" subtitle="基准原文、结构化拆解、行情覆盖与相对表现" rows={groups[4]} />
      <LedgerSection title="6. 宏观市场数据" subtitle="公共市场指标，以及按T+N对齐后计算的产品—市场历史响应" rows={groups[5]} />
      <LedgerSection title="7. 六类风险二次分析" subtitle="基于披露与行情的计算/推断结果，不是管理人原始披露" rows={groups[6]} />
      <LedgerSection title="8. 现有决策输出" subtitle="当前规则引擎产物，暂不因风险画像 V1 自动调整总分" rows={groups[7]} />

      <EvidenceList title="当前明确的数据缺口" rows={disclosure.data_gaps || []} />
    </div>
  );
}
